import {
  DarwinFlex,
  DTableProps,
  StatusChip,
  StatusChipColors,
  ActionsDropdown,
  CombinedText,
  ACTIONS_COL_WIDTH,
  DMenuProps,
  ColumnAction,
  renderNameCell,
} from '@darwin-ui';
import Icon from 'components/darwin-ui/Icon';
import { TFunction } from 'i18next';
import { Company, DataSharingStatus } from 'types/organizationTypes';

export const DELETE_ACTION_KEY = 'delete';

const statusChipColors = {
  [DataSharingStatus.PENDING]: StatusChipColors.yellow,
  [DataSharingStatus.ACCEPTED]: StatusChipColors.green,
  [DataSharingStatus.REJECTED]: StatusChipColors.red,
};

export const getColumns = (
  t: TFunction,
  actions: ColumnAction<Company>[],
  searchQuery: string
) => {
  const menuItems = (company: Company): DMenuProps['items'] =>
    actions
      .filter(action => {
        if (action.key === DELETE_ACTION_KEY && company.isPrimary) {
          return false;
        }
        return true;
      })
      .map(action => ({
        key: action.key,
        label: (
          <DarwinFlex
            gap={8}
            align="center"
            onClick={() => action?.onClick?.(company)}
          >
            <Icon
              name={action.name ? action.name : 'darwin-user'}
              size="sm"
              color="inherit"
            />
            {action.label}
          </DarwinFlex>
        ),
      }));

  const columns: DTableProps<Company>['columns'] = [
    {
      title: t('organization.organizations-list.columns.name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name, { isPrimary }) =>
        renderNameCell(name, isPrimary, searchQuery),
      width: 240,
    },
    {
      title: t('organization.organizations-list.columns.company-fiscal-id'),
      dataIndex: 'fiscalId',
      key: 'fiscalId',
      sorter: true,
      render: (fiscalId: string) => (
        <CombinedText lines={1} text={fiscalId} highlight={searchQuery} />
      ),
      width: 320,
    },
    {
      title: t('organization.organizations-list.columns.description'),
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      render: (description: string) => (
        <CombinedText lines={1} text={description} highlight={searchQuery} />
      ),
    },
    {
      title: t('organization.organizations-list.columns.address-count'),
      dataIndex: 'addressCount',
      key: 'addressCount',
      sorter: true,
      align: 'center',
      render: (addressCount: number) => addressCount,
      width: 200,
    },
    {
      title: t('organization.organizations-list.columns.location-count'),
      dataIndex: 'locationCount',
      key: 'locationCount',
      sorter: true,
      align: 'center',
      render: (locationCount: number) => locationCount,
      width: 200,
    },
    {
      title: t('organization.organizations-list.columns.data-sharing-status'),
      dataIndex: 'dataSharing',
      key: 'dataSharing',
      sorter: true,
      align: 'center',
      render: (_, record) =>
        record.dataSharing?.status && (
          <StatusChip
            color={statusChipColors[record.dataSharing.status]}
            label={record.dataSharing.status.toUpperCase()}
          />
        ),
      width: 250,
    },
    {
      title: t('organization.organizations-list.columns.organization-key'),
      dataIndex: 'organizationKey',
      key: 'organizationKey',
      sorter: true,
      render: (organizationKey: string) => (
        <CombinedText
          lines={1}
          text={organizationKey}
          highlight={searchQuery}
        />
      ),
      width: 320,
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_: any, company: Company) => (
        <ActionsDropdown item={company} getMenuItems={menuItems} />
      ),
      width: ACTIONS_COL_WIDTH,
    },
  ];

  return columns;
};
